import { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { useLocation } from '@/contexts/LocationContext';
import { useRole } from '@/hooks/use-role';
import { useProductionSettings } from '../hooks/useProductionSettings';
import { trpc } from '@/lib/trpc';
import {
  Loader2,
  Plus,
  Minus,
  Info,
  Archive,
  MapPin,
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

const MAX_BINS_PER_REQUEST = 500;

type BinSize = 'STANDARD' | 'LARGE' | 'HUGE';

interface BinTypeControlProps {
  title: string;
  description: string;
  binType: 'REGULAR' | 'OOS';
  binSize: BinSize;
  totalCount: number;
  emptyCount: number;
  latestLabel: string | null;
  onCreateBins: (count: number) => void;
  onDeleteBins: (count: number) => void;
  isCreating: boolean;
  isDeleting: boolean;
  isAdmin: boolean;
}

function BinTypeControl({
  title,
  description,
  binType,
  binSize,
  totalCount,
  emptyCount,
  latestLabel,
  onCreateBins,
  onDeleteBins,
  isCreating,
  isDeleting,
  isAdmin,
}: BinTypeControlProps) {
  const [count, setCount] = useState<number | ''>(5);

  const effectiveCount = count === '' ? 0 : count;
  const deleteDisabled = emptyCount === 0 || isDeleting || !isAdmin;

  const deleteTooltip = !isAdmin
    ? 'Admin only'
    : emptyCount === 0
      ? 'No empty bins to delete'
      : `Delete bins (${emptyCount} empty)`;

  const id = `${binType}-${binSize}`.toLowerCase();

  return (
    <div className="space-y-3">
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <Archive className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
            <span className="text-base font-medium">{title}</span>
          </div>
          <p className="text-sm text-muted-foreground">{description}</p>
        </div>
        <div className="text-right shrink-0">
          <p className="text-2xl font-semibold tabular-nums">{totalCount}</p>
          {latestLabel && (
            <p className="text-xs text-muted-foreground">Latest: {latestLabel}</p>
          )}
        </div>
      </div>

      <div className="flex items-center justify-center gap-3">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="outline"
                      size="icon"
                      disabled={deleteDisabled}
                      className="h-11 w-11 select-none"
                      aria-label={`Remove ${effectiveCount} ${title.toLowerCase()}`}
                    >
                      {isDeleting ? (
                        <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
                      ) : (
                        <Minus className="h-5 w-5" aria-hidden="true" />
                      )}
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete {title}?</AlertDialogTitle>
                      <AlertDialogDescription>
                        This will permanently delete the {Math.min(effectiveCount, emptyCount)} highest-numbered
                        empty bin{Math.min(effectiveCount, emptyCount) !== 1 ? 's' : ''}.
                        Bins with garments assigned will not be affected. This action cannot be undone.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={() => onDeleteBins(Math.min(effectiveCount, emptyCount))}>
                        Delete Bins
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </span>
            </TooltipTrigger>
            <TooltipContent>{deleteTooltip}</TooltipContent>
          </Tooltip>
        </TooltipProvider>

        <Input
          id={`${id}-bin-count`}
          type="number"
          inputMode="numeric"
          min={1}
          max={MAX_BINS_PER_REQUEST}
          value={count}
          onChange={(e) => {
            if (e.target.value === '') {
              setCount('');
              return;
            }
            const val = parseInt(e.target.value);
            if (!isNaN(val)) {
              setCount(Math.max(1, Math.min(MAX_BINS_PER_REQUEST, val)));
            }
          }}
          onBlur={() => {
            if (count === '' || count < 1) setCount(1);
          }}
          className="h-11 w-24 text-center text-lg font-medium tabular-nums"
          aria-label={`Number of ${title.toLowerCase()}`}
        />

        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="icon"
                onClick={() => onCreateBins(effectiveCount)}
                disabled={isCreating || effectiveCount < 1}
                className="h-11 w-11 select-none"
                aria-label={`Create ${effectiveCount} ${title.toLowerCase()}`}
              >
                {isCreating ? (
                  <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
                ) : (
                  <Plus className="h-5 w-5" aria-hidden="true" />
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent>Create bins</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      {emptyCount > 0 && (
        <p className="text-xs text-muted-foreground text-center">
          {emptyCount} empty bin{emptyCount !== 1 ? 's' : ''} available to delete
        </p>
      )}
    </div>
  );
}

export function BinCreationSection() {
  const { toast } = useToast();
  const { selectedLocation } = useLocation();
  const { isAdmin } = useRole();
  const { settings, updateSetting } = useProductionSettings();

  const trpcContext = trpc.useUtils();
  const locationId = selectedLocation?.id ?? '';

  const binStatsQuery = trpc.bins.getBinStats.useQuery(
    { locationId },
    { enabled: !!locationId }
  );

  // Fetch bins for each category to track empty counts
  const regularBinsQuery = trpc.bins.getBins.useQuery(
    { locationId, binType: 'REGULAR' },
    { enabled: !!locationId }
  );

  const oosBinsQuery = trpc.bins.getBins.useQuery(
    { locationId, binType: 'OOS' },
    { enabled: !!locationId }
  );

  const createBinsMutation = trpc.bins.createBins.useMutation({
    onSuccess: (result) => {
      toast({ title: 'Bins created', description: result.message, duration: 3000 });
      trpcContext.bins.getBinStats.invalidate();
      trpcContext.bins.getBins.invalidate();
    },
    onError: (error) => {
      toast({ title: 'Failed to create bins', description: error.message, variant: 'destructive', duration: 5000 });
    },
  });

  const deleteBinsMutation = trpc.bins.deleteBins.useMutation({
    onSuccess: (result) => {
      toast({ title: 'Bins deleted', description: result.message, duration: 3000 });
      trpcContext.bins.getBinStats.invalidate();
      trpcContext.bins.getBins.invalidate();
    },
    onError: (error) => {
      toast({ title: 'Failed to delete bins', description: error.message, variant: 'destructive', duration: 5000 });
    },
  });

  // Filter bins by size for empty count tracking
  const allRegularBins = regularBinsQuery.data ?? [];
  const standardBins = allRegularBins.filter(b => !b.binSize || b.binSize === 'STANDARD');
  const largeBins = allRegularBins.filter(b => b.binSize === 'LARGE');
  const hugeBins = allRegularBins.filter(b => b.binSize === 'HUGE');
  const oosBins = oosBinsQuery.data ?? [];

  const standardEmptyCount = standardBins.filter(b => b.status === 'EMPTY').length;
  const largeEmptyCount = largeBins.filter(b => b.status === 'EMPTY').length;
  const hugeEmptyCount = hugeBins.filter(b => b.status === 'EMPTY').length;
  const oosEmptyCount = oosBins.filter(b => b.status === 'EMPTY').length;

  const getEmptyBinIds = (bins: typeof allRegularBins, count: number): string[] => {
    if (!bins) return [];
    const emptyBins = bins
      .filter(b => b.status === 'EMPTY')
      .sort((a, b) => b.label.localeCompare(a.label));
    return emptyBins.slice(0, count).map(b => b.id);
  };

  const handleCreateBins = (binType: 'REGULAR' | 'OOS', binSize: BinSize, count: number) => {
    if (!locationId) return;
    createBinsMutation.mutate({ locationId, count, binType, binSize });
  };

  const handleDeleteBins = (bins: typeof allRegularBins, count: number) => {
    if (!locationId) return;
    const binIds = getEmptyBinIds(bins, count);
    if (binIds.length === 0) {
      toast({
        title: 'No empty bins',
        description: 'There are no empty bins to delete.',
        duration: 3000,
      });
      return;
    }
    deleteBinsMutation.mutate({ locationId, binIds });
  };

  if (!selectedLocation?.id) {
    return (
      <div
        className="flex flex-col items-center justify-center py-8 text-center"
        role="status"
        aria-label="No location selected"
      >
        <MapPin className="h-10 w-10 text-muted-foreground/50 mb-3" aria-hidden="true" />
        <p className="text-muted-foreground mb-2">
          Select a location from the header to manage bins
        </p>
        <p className="text-sm text-muted-foreground">
          <kbd className="px-1.5 py-0.5 bg-muted rounded border text-xs">Tab</kbd> to navigation, then use{' '}
          <kbd className="px-1.5 py-0.5 bg-muted rounded border text-xs">Enter</kbd> to select a location
        </p>
      </div>
    );
  }

  const stats = binStatsQuery.data;

  if (binStatsQuery.isPending) {
    return (
      <div className="flex items-center justify-center py-8" role="status" aria-busy="true">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-hidden="true" />
        <span className="ml-2 text-muted-foreground">Loading bin statistics...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <BinTypeControl
        title="Standard Bins"
        description="Default bins for most orders. Labels like BIN-001, BIN-002..."
        binType="REGULAR"
        binSize="STANDARD"
        totalCount={stats?.regularCount ?? 0}
        emptyCount={standardEmptyCount}
        latestLabel={stats?.maxRegularLabel ?? null}
        onCreateBins={(count) => handleCreateBins('REGULAR', 'STANDARD', count)}
        onDeleteBins={(count) => handleDeleteBins(standardBins, count)}
        isCreating={createBinsMutation.isPending}
        isDeleting={deleteBinsMutation.isPending}
        isAdmin={isAdmin()}
      />

      <Separator />

      <BinTypeControl
        title="Large Bins"
        description="For hoodie orders and 5+ item orders. Labels like LRG-001, LRG-002..."
        binType="REGULAR"
        binSize="LARGE"
        totalCount={stats?.largeCount ?? 0}
        emptyCount={largeEmptyCount}
        latestLabel={stats?.maxLargeLabel ?? null}
        onCreateBins={(count) => handleCreateBins('REGULAR', 'LARGE', count)}
        onDeleteBins={(count) => handleDeleteBins(largeBins, count)}
        isCreating={createBinsMutation.isPending}
        isDeleting={deleteBinsMutation.isPending}
        isAdmin={isAdmin()}
      />

      <Separator />

      <BinTypeControl
        title="Huge Bins"
        description="For rug orders and oversized items. Labels like HGE-001, HGE-002..."
        binType="REGULAR"
        binSize="HUGE"
        totalCount={stats?.hugeCount ?? 0}
        emptyCount={hugeEmptyCount}
        latestLabel={stats?.maxHugeLabel ?? null}
        onCreateBins={(count) => handleCreateBins('REGULAR', 'HUGE', count)}
        onDeleteBins={(count) => handleDeleteBins(hugeBins, count)}
        isCreating={createBinsMutation.isPending}
        isDeleting={deleteBinsMutation.isPending}
        isAdmin={isAdmin()}
      />

      <Separator />

      <BinTypeControl
        title="OOS Bins"
        description="Hold items awaiting inventory (Out of Stock). Labels like OOS-001, OOS-002..."
        binType="OOS"
        binSize="STANDARD"
        totalCount={stats?.oosCount ?? 0}
        emptyCount={oosEmptyCount}
        latestLabel={stats?.maxOosLabel ?? null}
        onCreateBins={(count) => handleCreateBins('OOS', 'STANDARD', count)}
        onDeleteBins={(count) => handleDeleteBins(oosBins, count)}
        isCreating={createBinsMutation.isPending}
        isDeleting={deleteBinsMutation.isPending}
        isAdmin={isAdmin()}
      />

      <Separator />

      <div className="flex items-center justify-between gap-3">
        <div className="space-y-1">
          <Label htmlFor="show-oos-bins" className="text-base font-medium">
            Show OOS Bins on Dashboard
          </Label>
          <p className="text-sm text-muted-foreground/90">
            Display out-of-stock bins in a separate card on the Bin Dashboard
          </p>
        </div>
        <Switch
          id="show-oos-bins"
          checked={settings.showOosBins}
          onCheckedChange={(checked) => updateSetting('showOosBins', checked)}
        />
      </div>

      <Separator />

      <div className="bg-muted/50 p-4 rounded-lg">
        <div className="flex items-start gap-2">
          <Info className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" aria-hidden="true" />
          <div className="text-sm text-muted-foreground space-y-1">
            <p>
              <strong>Bin labels</strong> auto-increment from the last created bin per size.
            </p>
            <p>
              <strong>Auto-assignment:</strong> Rug orders get Huge bins, hoodie/5+ item orders get Large bins, everything else gets Standard. If no bins of the right size are available, the system tries the next size up.
            </p>
            <p>
              <strong>Limit:</strong> Maximum {MAX_BINS_PER_REQUEST} bins per request,
              10,000 bins per location per type.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
